import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Hardcoded demo credentials
const DEMO_USERS = {
  'usuario@foodloop.com': {
    password: 'usuario123',
    user: {
      email: 'usuario@foodloop.com',
      id: 'user-1',
      user_metadata: { full_name: 'Usuario Demo', role: 'consumer' }
    },
    redirectPath: '/consumer-dashboard'
  },
  'empresa@foodloop.com': {
    password: 'empresa123', 
    user: {
      email: 'empresa@foodloop.com',
      id: 'business-1',
      user_metadata: { full_name: 'Empresa Demo', role: 'business' }
    },
    redirectPath: '/business-dashboard'
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('demo_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('demo_user');
      }
    }
    setLoading(false);
  }, []);

  const signIn = async (email, password) => {
    setLoading(true);
    setConnectionError(null);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      const demoUser = DEMO_USERS?.[email];
      
      if (!demoUser) {
        throw new Error('Usuario no encontrado. Usa usuario@foodloop.com o empresa@foodloop.com');
      }

      if (demoUser?.password !== password) {
        throw new Error('Contraseña incorrecta. Usa usuario123 para usuario o empresa123 para empresa');
      }

      // Store user in localStorage and state
      localStorage.setItem('demo_user', JSON.stringify(demoUser?.user));
      setUser(demoUser?.user);
      
      return { 
        data: { user: demoUser?.user }, 
        error: null,
        redirectPath: demoUser?.redirectPath
      };
      
    } catch (err) {
      setConnectionError(null);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName) => {
    setLoading(true);
    setConnectionError(null);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // For demo, just create a temporary user
      const newUser = {
        email,
        id: `temp-${Date.now()}`,
        user_metadata: { full_name: fullName, role: 'consumer' }
      };

      localStorage.setItem('demo_user', JSON.stringify(newUser));
      setUser(newUser);
      
      return { 
        data: { user: newUser }, 
        error: null 
      };
      
    } catch (err) {
      setConnectionError(null);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      localStorage.removeItem('demo_user');
      setUser(null);
      setConnectionError(null);
      return { error: null };
    } catch (err) {
      console.error('SignOut error:', err);
      // Force local signout
      setUser(null);
      return { error: null };
    }
  };

  const value = {
    user,
    loading,
    connectionError,
    isConfigured: true, // Always configured for demo mode
    signIn,
    signUp,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};